/* GET /api/projects and /api/projects/{id}/rounds - 15 sections 3.4a, 3.4b.
 * Our projects, with Kanopy's as rounds beneath them. A tree or span is one row per round,
 * so every figure here is as of the latest round (15 section 3.4a).
 */
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  PROJECT_DEFS,
  projects as projectCorpus,
  riskStepForSpan,
  roundsByProject,
  thresholdForSpan,
  spans as spanCorpus,
} from "../mocks/corpus";
import { fetchPage, paginate, useAllPages, type PageArgs } from "./envelope";
import { countSteps, measuredCount, stepShare } from "../lib/risk";
import type { CaptureRound, Page, Project, RiskStep } from "./types";

export interface ProjectFilters {
  closed?: boolean; // omitted returns live projects only (15 section 3.4a)
}

export async function listProjects(filters: ProjectFilters = {}, pageArgs: PageArgs = {}): Promise<Page<Project>> {
  const closed = filters.closed ?? false;
  const sorted = projectCorpus
    .filter((p) => (p.closed_at !== null) === closed)
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  return paginate(sorted, pageArgs);
}

export async function getProject(id: string): Promise<Project | undefined> {
  return projectCorpus.find((p) => p.id === id);
}

export interface ProjectExposureRow {
  projectId: string;
  name: string;
  lengthM: number; // every span in the latest round, measured or not
  spanCount: number; // every span too; measuredCount(riskCounts) is the measured ones
  measuredLengthM: number; // the spans that have a clearance, which every share is over
  riskCounts: Record<RiskStep, number>;
  criticalShare: number;
  meanClearanceM: number | null; // over the spans that have a clearance; null where none does
  thresholdM: number | null; // null where the round's spans carry more than one class
  cadenceYears: number;
  latestCapturedAt: string | null;
  latestRoundArchived: boolean;
}

/* The exposure ranking, sorted by Critical share. D04, reports and cadence
 * screens read these rows rather than counting spans again
 * (CLAUDE.md rule 3); D03 reads the live projects instead, which carry no
 * risk figures. An archived reflight is a row too: whether it should count is
 * open in 02's Open table. */
export async function listProjectExposure(): Promise<ProjectExposureRow[]> {
  const rows = projectCorpus.map((p) => {
    const def = PROJECT_DEFS.find((d) => d.name === p.name);
    const latest = latestRound(p, roundsByProject);
    const projectSpans = spanCorpus.filter((s) => s.capture_round_id === p.latest_round_id);
    // An unmeasured span is in spanCount and in no step.
    const riskCounts = countSteps(projectSpans.map(riskStepForSpan).filter((step) => step !== null));
    const measuredSpans = projectSpans.filter((s) => s.min_clearance_m !== null);
    const clearances = measuredSpans.map((s) => s.min_clearance_m).filter((m) => m !== null);
    const classes = new Set(projectSpans.map((s) => s.voltage_class));
    return {
      projectId: p.id,
      name: p.name,
      lengthM: projectSpans.reduce((sum, s) => sum + s.length_m, 0),
      spanCount: projectSpans.length,
      measuredLengthM: measuredSpans.reduce((sum, s) => sum + s.length_m, 0),
      riskCounts,
      criticalShare: stepShare(riskCounts, "Critical"),
      meanClearanceM: clearances.length ? clearances.reduce((sum, m) => sum + m, 0) / clearances.length : null,
      // A threshold is per class, so a project names one only where its spans
      // share a class; with more than one there is no single line to draw.
      thresholdM: classes.size === 1 ? thresholdForSpan(projectSpans[0]) : null,
      cadenceYears: def?.historicalCadenceYears ?? 5,
      latestCapturedAt: p.latest_captured_at,
      latestRoundArchived: latest?.archived_at != null,
    };
  });
  return rows.sort((a, b) => b.criticalShare - a.criticalShare);
}

export function useProjects(filters: ProjectFilters = {}, pageArgs: PageArgs = {}) {
  return useQuery({
    queryKey: ["projects", filters, pageArgs],
    queryFn: () => listProjects(filters, pageArgs),
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ["project", id],
    queryFn: () => getProject(id!),
    enabled: id !== undefined,
  });
}

/* Live projects from the real `/api/projects`. A reader of projects that does
 * not use this is on the mock functions above, which go when their screens
 * move to the real API. `search` narrows to the projects whose name or
 * utility's name holds it (15 section 3.4a). */
export function useMirrorProjects(search = "") {
  return useAllPages<Project>(["mirror", "projects"], "/api/projects", search ? { search } : {});
}

/* One project's rounds from the real `/api/projects/{id}/rounds`. A round list
 * is one page; a project that outgrew it is refused rather than shown
 * truncated, since a missing round reads as one never flown. */
async function fetchMirrorRounds(projectId: string): Promise<CaptureRound[]> {
  const page = await fetchPage<CaptureRound>(`/api/projects/${encodeURIComponent(projectId)}/rounds`, {}, null);
  if (page.next_cursor !== null) {
    throw new Error("This project has more rounds than one page holds.");
  }
  return page.items;
}

/* Every listed project's rounds, newest first, keyed by project. Undefined
 * until all have arrived, so nothing is drawn from half of them. */
export function useMirrorRounds(projectIds: string[]): {
  rounds: Map<string, CaptureRound[]> | undefined;
  error: Error | null;
} {
  return useQueries({
    queries: projectIds.map((id) => ({
      queryKey: ["mirror", "rounds", id],
      queryFn: () => fetchMirrorRounds(id),
    })),
    combine: (results) => ({
      rounds: results.every((r) => r.data !== undefined)
        ? new Map(projectIds.map((id, i) => [id, results[i].data!]))
        : undefined,
      error: results.find((r) => r.error)?.error ?? null,
    }),
  });
}

export interface LiveProjectRow extends Project {
  latestRoundArchived: boolean;
}

/* A project's latest round among its rounds, or undefined where it has none.
 * Read by the round's id rather than by position, so it is the round
 * `latest_captured_at` is dated from (15 sections 3.4a, 3.4b). */
export function latestRound(project: Project, rounds: Map<string, CaptureRound[]>): CaptureRound | undefined {
  return rounds.get(project.id)?.find((r) => r.id === project.latest_round_id);
}

/* A live project beside whether its latest round is archived, which the
 * project carries no field for and its rounds do (15 sections 3.4a, 3.4b).
 * A project with no round has none to be archived. */
export function liveProjectRows(projects: Project[], rounds: Map<string, CaptureRound[]>): LiveProjectRow[] {
  return projects.map((p) => {
    const latest = latestRound(p, rounds);
    return { ...p, latestRoundArchived: latest?.archived_at != null };
  });
}

/* The Critical share above which a project is flagged for a shortened cadence
 * (Product Screens slide 25). D03's tile and its footnote both read this. */
export const CRITICAL_SHARE_TARGET = 0.2;

/* A project's Critical share of its measured spans, through lib/risk.ts like
 * every share, from the counts the server serves (15 section 3.4a). Null
 * where nothing is measured, because zero would read as no exposure (15
 * section 1.4). */
export function criticalShare(p: Project): number | null {
  return measuredCount(p.span_verdict_counts) === 0 ? null : stepShare(p.span_verdict_counts, "Critical");
}

/* D03's figures across its projects, over each project's share rather than
 * over spans again. A project with nothing measured has no share, so it is
 * above no target and in no mean; the mean is null where no project has a
 * share. */
export function acrossProjects(rows: Project[]): {
  aboveTarget: number;
  meanCriticalShare: number | null;
  measuredLengthM: number;
} {
  const shares = rows.map(criticalShare).filter((share) => share !== null);
  return {
    aboveTarget: shares.filter((share) => share > CRITICAL_SHARE_TARGET).length,
    meanCriticalShare: shares.length ? shares.reduce((sum, share) => sum + share, 0) / shares.length : null,
    measuredLengthM: rows.reduce((sum, p) => sum + (p.measured_length_m ?? 0), 0),
  };
}

/* D03's order: Critical share, highest first, because the list exists to
 * prioritise (Product Screens slide 25). A project with no share sorts last,
 * and ties keep the API's name order. */
export function byCriticalShare<T extends Project>(rows: T[]): T[] {
  return [...rows].sort((a, b) => (criticalShare(b) ?? -1) - (criticalShare(a) ?? -1));
}

/* The projects either side of one in D03's order, for Project detail's
 * Previous and Next, as a span's and a tree's neighbours are their list's.
 * Taken from the live projects already loaded, because the API serves no
 * neighbour for a project, and through byCriticalShare so the order is the
 * one D03 shows. Null at either end, and both null for a project not listed. */
export function projectNeighbours(rows: Project[], id: string): { previous: string | null; next: string | null } {
  const ordered = byCriticalShare(rows);
  const at = ordered.findIndex((p) => p.id === id);
  if (at === -1) return { previous: null, next: null };
  return { previous: ordered[at - 1]?.id ?? null, next: ordered[at + 1]?.id ?? null };
}

/* D03's rows: every live project the search matches, with its rounds.
 * Undefined until both have arrived, for the reason useMirrorRounds gives. The
 * rounds share the map's query keys, so moving between it and D03 reads them
 * once. */
export function useLiveProjectRows(search = ""): { rows: LiveProjectRow[] | undefined; error: Error | null } {
  const projects = useMirrorProjects(search);
  const projectIds = useMemo(() => (projects.items ?? []).map((p) => p.id), [projects.items]);
  const rounds = useMirrorRounds(projectIds);
  const rows = useMemo(
    () => (projects.items && rounds.rounds ? liveProjectRows(projects.items, rounds.rounds) : undefined),
    [projects.items, rounds.rounds],
  );
  return { rows, error: projects.error ?? rounds.error };
}

export function useProjectExposure() {
  return useQuery({ queryKey: ["project-exposure"], queryFn: listProjectExposure });
}
